import { Almanac } from "protos/robot_syncer/plant_profile";
import { classes } from "portal/theme/theme";
import {
  getFormulasFromBands,
  getShootTimeBands,
  getShootTimeDomain,
  getSizesFromBands,
  isValidAlmanac,
  ShootTimeBand,
  ShootTimeFormulaVersion,
} from "portal/utils/shootTime";
import { getNiceCeiling, SHOOT_TIME_CHART_PADDING } from "./layout";
import { ShootTimeChartBands } from "./ShootTimeChartBands";
import { ShootTimeChartLegend } from "./ShootTimeChartLegend";
import { ShootTimeChartXAxis } from "./ShootTimeChartXAxis";
import { ShootTimeChartYAxis } from "./ShootTimeChartYAxis";
import { ShootTimeComparisonLine } from "./ShootTimeComparisonLine";
import { ShootTimeEnvelope } from "./ShootTimeEnvelope";
import { ShootTimeFormulaLine } from "./ShootTimeFormulaLine";
import { ThresholdDividers } from "./thresholds/ThresholdDividers";
import { ThresholdHandles } from "./thresholds/ThresholdHandles";
import { ThresholdInputsOverlay } from "./thresholds/ThresholdInputsOverlay";
import { ThresholdPills } from "./thresholds/ThresholdPills";
import { useResizeObserver } from "portal/utils/hooks/useResizeObserver";
import { useTheme } from "@mui/material";
import { useThresholdDragState } from "./thresholds/useThresholdDragState";
import { useTranslation } from "react-i18next";
import { WhenTrue } from "common/utils/types";
import React, { useId, useMemo, useRef } from "react";

export type ShootTimeChartProps = {
  bands: ShootTimeBand[];
  className?: string;
  comparisonAlmanac?: Almanac;
  comparisonLabel?: string;
  formulaVersion: ShootTimeFormulaVersion;
} & WhenTrue<
  "canEdit",
  {
    onSizesChange: (sizes: number[]) => void;
    onFieldValidityChange: (fieldId: string, isValid: boolean) => void;
  }
>;

export const ShootTimeChart = ({
  bands,
  canEdit,
  className,
  comparisonAlmanac,
  comparisonLabel,
  formulaVersion,
  onSizesChange,
  onFieldValidityChange,
}: ShootTimeChartProps): React.ReactElement => {
  const { t } = useTranslation();
  const {
    palette: {
      chart: { bandStrokeColors, plotBackground },
    },
  } = useTheme();
  const plotClipPathId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { width: containerWidth, height: containerHeight } =
    useResizeObserver(containerRef);

  const committedMaxRadiusMm = useMemo(
    () => getShootTimeDomain(bands, formulaVersion).maxRadiusMm,
    [bands, formulaVersion]
  );

  const {
    activeThresholdIndex,
    displaySizes,
    onActivateThreshold,
    onDragStart,
    onDragMove,
    onDragEnd,
  } = useThresholdDragState(
    bands,
    committedMaxRadiusMm,
    canEdit ? onSizesChange : undefined
  );

  // uses display sizes if you're in the middle of dragging, else committed bands (this is all for rendering performance reasons)
  const displayBands = useMemo(() => {
    const committedSizes = getSizesFromBands(bands);
    const sizesMatch =
      displaySizes.length === committedSizes.length &&
      displaySizes.every((size, i) => size === committedSizes[i]);
    if (sizesMatch) {
      return bands;
    }
    return getShootTimeBands(
      t,
      displaySizes,
      getFormulasFromBands(bands),
      bandStrokeColors
    );
  }, [bandStrokeColors, bands, displaySizes, t]);

  const { maxRadiusMm, maxLaserTime } = useMemo(
    () => getShootTimeDomain(displayBands, formulaVersion),
    [displayBands, formulaVersion]
  );

  const validComparisonAlmanac = isValidAlmanac(comparisonAlmanac)
    ? comparisonAlmanac
    : undefined;

  const width = containerWidth;
  const chartHeight = containerHeight;
  const chartHasSize = width > 0 && chartHeight > 0 && displayBands.length > 0;
  const plotLeft = SHOOT_TIME_CHART_PADDING.left;
  const plotWidth =
    width - SHOOT_TIME_CHART_PADDING.left - SHOOT_TIME_CHART_PADDING.right;
  const plotHeight =
    chartHeight -
    SHOOT_TIME_CHART_PADDING.top -
    SHOOT_TIME_CHART_PADDING.bottom;
  const yMax = getNiceCeiling(maxLaserTime);

  const xScale = (mm: number): number =>
    plotLeft + (mm / maxRadiusMm) * plotWidth;
  const yScale = (ms: number): number =>
    SHOOT_TIME_CHART_PADDING.top + plotHeight - (ms / yMax) * plotHeight;

  return (
    <div
      ref={containerRef}
      className={classes(
        "relative w-full min-w-0 h-[300px] rounded-sm md:h-[320px]",
        className
      )}
      style={{ backgroundColor: plotBackground }}
    >
      {chartHasSize && (
        <>
          <svg
            width={width}
            height={chartHeight}
            viewBox={`0 0 ${width} ${chartHeight}`}
            className="block max-w-full"
            role="img"
            aria-label={t("views.categoryTemplates.tabs.shootTime")}
          >
            <defs>
              <clipPath id={plotClipPathId}>
                <rect
                  x={plotLeft}
                  y={SHOOT_TIME_CHART_PADDING.top}
                  width={plotWidth}
                  height={plotHeight}
                />
              </clipPath>
            </defs>
            <ShootTimeChartBands
              bands={displayBands}
              chartPadding={SHOOT_TIME_CHART_PADDING}
              plotHeight={plotHeight}
              xScale={xScale}
            />
            <ShootTimeChartYAxis
              plotLeft={plotLeft}
              plotWidth={plotWidth}
              yMax={yMax}
              yScale={yScale}
            />
            <ThresholdDividers
              bands={displayBands}
              chartPadding={SHOOT_TIME_CHART_PADDING}
              displaySizes={displaySizes}
              plotHeight={plotHeight}
              xScale={xScale}
            />
            <g clipPath={`url(#${plotClipPathId})`}>
              {formulaVersion === "2.5" && (
                <ShootTimeEnvelope
                  bands={displayBands}
                  maxRadiusMm={maxRadiusMm}
                  plotWidth={plotWidth}
                  xScale={xScale}
                  yScale={yScale}
                />
              )}
              {validComparisonAlmanac && (
                <ShootTimeComparisonLine
                  almanac={validComparisonAlmanac}
                  comparisonLabel={comparisonLabel}
                  maxRadiusMm={maxRadiusMm}
                  plotWidth={plotWidth}
                  xScale={xScale}
                  yScale={yScale}
                />
              )}
              <ShootTimeFormulaLine
                bands={displayBands}
                maxRadiusMm={maxRadiusMm}
                formulaVersion={formulaVersion}
                plotWidth={plotWidth}
                xScale={xScale}
                yScale={yScale}
              />
            </g>
            <ShootTimeChartXAxis
              bands={displayBands}
              chartHeight={chartHeight}
              plotLeft={plotLeft}
              plotWidth={plotWidth}
              xScale={xScale}
            />
            <ShootTimeChartLegend
              bands={displayBands}
              chartHeight={chartHeight}
              comparisonLabel={comparisonLabel}
              formulaVersion={formulaVersion}
            />
            {canEdit ? (
              <ThresholdHandles
                bands={displayBands}
                chartPadding={SHOOT_TIME_CHART_PADDING}
                chartWidth={width}
                displaySizes={displaySizes}
                maxRadiusMm={maxRadiusMm}
                plotHeight={plotHeight}
                plotLeft={plotLeft}
                plotWidth={plotWidth}
                xScale={xScale}
                onDragStart={onDragStart}
                onDragMove={onDragMove}
                onDragEnd={onDragEnd}
              />
            ) : (
              <ThresholdPills
                bands={displayBands}
                chartPadding={SHOOT_TIME_CHART_PADDING}
                displaySizes={displaySizes}
                xScale={xScale}
              />
            )}
          </svg>
          {canEdit && (
            <ThresholdInputsOverlay
              activeThresholdIndex={activeThresholdIndex}
              bands={displayBands}
              displaySizes={displaySizes}
              maxRadiusMm={maxRadiusMm}
              onActivateThreshold={onActivateThreshold}
              onFieldValidityChange={onFieldValidityChange}
              onSizesChange={onSizesChange}
              plotTop={SHOOT_TIME_CHART_PADDING.top}
              xScale={xScale}
            />
          )}
        </>
      )}
    </div>
  );
};
